import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { PlatformService } from "@obg/ngx.global.evo";
import { ignoreNil } from "@obg/rxjs";
import { Observable, of } from "rxjs";
import { filter, map, switchMap } from "rxjs/operators";

import { OPTIMIZELY_FEATURE_FLAGS } from "../const";

import { FeatureFlag, OptimizelyFeatureFlags, OptimizelyFeatureFlagsConfig } from "./feature-flag.model";

@Injectable({
	providedIn: "root",
})
export class FeatureFlagService {
	constructor(
		private httpClient: HttpClient,
		private platformService: PlatformService,
		@Inject(OPTIMIZELY_FEATURE_FLAGS) private optimizelyFeatureFlags: OptimizelyFeatureFlags
	) {}

	/**
	 * The following function returns with the selected feature flag value by the key parameter or 'undefined'
	 * @param key @type {string}
	 * @returns {FeatureFlag<T> | undefined}
	 */
	get<T>(key: string): FeatureFlag<T> | undefined {
		return this.optimizelyFeatureFlags.featureFlags[key];
	}

	/**
	 * The following function returns with an Observable with selected feature flag value by the key parameter or 'undefined'
	 * The config contains the following options:
	 * - ***defaultValue***: it is possible to add default value to the stream when the feature flag object is 'undefined' (it can be 'undefined' during the SSR)
	 * Warning: it can cause flaky rendering in some cases
	 * - ***returnDefaultWhenIsUndefined***: if the following flag is 'true' it will stream the default value until the feature flag is 'undefined'
	 * - ***isEnabledOnlyClientSide***: if the following flag is 'true' the stream will only emit value in client side
	 * @param key @type {string}
	 * @param config @type {OptimizelyFeatureFlagsConfig<T>}
	 * @returns {Observable<FeatureFlag<T> | undefined>}
	 */
	get$<T>(key: string, config?: OptimizelyFeatureFlagsConfig<T>): Observable<FeatureFlag<T> | undefined> {
		if (!config) {
			return this.optimizelyFeatureFlags.featureFlags$.pipe(
				ignoreNil(),
				map((featureFlags) => featureFlags[key])
			);
		}

		const selectValueByConfig = (
			featureFlag: FeatureFlag<T> | undefined,
			defaultValue: FeatureFlag<T>,
			returnDefaultWhenIsUndefined?: boolean
		): FeatureFlag<T> | undefined => {
			if (!returnDefaultWhenIsUndefined) {
				return featureFlag;
			}

			return featureFlag ? featureFlag : defaultValue;
		};

		const { defaultValue, returnDefaultWhenIsUndefined, isEnabledOnlyClientSide } = config;

		return this.optimizelyFeatureFlags.featureFlags$.pipe(
			filter(() => (isEnabledOnlyClientSide ? this.platformService.isBrowser : true)),
			switchMap((featureFlags) =>
				featureFlags
					? of(selectValueByConfig(featureFlags[key], defaultValue, returnDefaultWhenIsUndefined))
					: of(defaultValue)
			)
		);
	}

	trackEvent = (eventName: string, eventTags: Record<string, any>): Observable<any> => {
		return this.httpClient.post("/track-optimizely-event", {
			eventName,
			eventTags,
		});
	};
}
